import PageBackTitle from "../Component/PageBackTitle";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import API_URL from "../../src/Config";
import { FiChevronRight, FiChevronDown } from "react-icons/fi";
import Snackbar from "@mui/material/Snackbar"; // Ensure you import Snackbar
import "./UserAccessTable.css";
import MuiAlert from "@mui/material/Alert";

const MenuItem = ({ item, level, onToggle, expanded, checked, onCheck }) => {
  const hasSubItems = item.sub_menu && item.sub_menu.length > 0;
  const isExpanded = expanded[item.id];
  return (
    <>
      <tr className="table-row">
        <td className="table-cell" style={{ paddingLeft: `${level * 20}px` }}>
          <div className="menu-item">
            <input
              type="checkbox"
              id={item.id}
              checked={checked[item.id] || false}
              onChange={(e) => {
                onCheck(item.id, e.target.checked, item); // Pass the item to handle submenus
              }}
              className="checkbox"
            />
            {hasSubItems && (
              <button onClick={() => onToggle(item.id)} className="toggle-btn">
                {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
              </button>
            )}
            <label htmlFor={item.id} className="menu-label">
              {item.name}
            </label>
          </div>
        </td>
      </tr>
      {hasSubItems &&
        isExpanded &&
        item.sub_menu.map((subItem) => (
          <MenuItem
            key={subItem.id}
            item={subItem}
            level={level + 1}
            onToggle={onToggle}
            expanded={expanded}
            checked={checked}
            onCheck={onCheck}
          />
        ))}
    </>
  );
};

function UserAccessTable({ isSidebarExpanded }) {
  const [menuItems, setMenuItems] = useState([]); // All menu items
  const [accessMenuItems, setAccessMenuItems] = useState([]); // Accessible menu items
  const [expanded, setExpanded] = useState({});
  const [checked, setChecked] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [severity, setSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const { ID } = useParams(); // Role ID
  const navigate = useNavigate();

  const showSnackbar = (message, nextSeverity = "success") => {
    setSnackbarMessage(message);
    setSeverity(nextSeverity);
    setSnackbarOpen(true);
  };

  const collectMenuItemIds = (items = {}, result = {}) => {
    const normalizedItems = Array.isArray(items) ? items : [items];

    normalizedItems.forEach((item) => {
      if (!item) return;

      result[item.id] = true;

      if (Array.isArray(item.sub_menu) && item.sub_menu.length > 0) {
        collectMenuItemIds(item.sub_menu, result);
      }
    });

    return result;
  };

  const setDescendantChecks = (item, isChecked, updatedChecked) => {
    if (!Array.isArray(item.sub_menu)) return;

    item.sub_menu.forEach((subItem) => {
      updatedChecked[subItem.id] = isChecked;
      setDescendantChecks(subItem, isChecked, updatedChecked);
    });
  };

  const fetchData = async () => {
    try {
      const authKey = localStorage.getItem("authKey");
      const response = await axios.get(
        `${API_URL}/api/user_access/get_menu_items`,
        {
          headers: { Authorization: authKey },
        }
      );
      setMenuItems(response.data);
    } catch (error) {
      console.error("Error fetching all menu items:", error.message);
      setError(error.message);
    }
  };

  const fetchAccessRoles = async () => {
    try {
      const authKey = localStorage.getItem("authKey");
      const response = await axios.get(
        `${API_URL}/api/get_menu_items_access/${ID}`,
        {
          headers: { Authorization: authKey },
        }
      );

      const accessData = response.data;

      if (Array.isArray(accessData) && accessData.length > 0) {
        setAccessMenuItems(accessData);

        setChecked(collectMenuItemIds(accessData));
      } else {
        // No access found, fallback to full menu list
        console.warn("No access data found, fetching all menu items...");
        await fetchData();
        setChecked({});
      }
    } catch (error) {
      if (error.response?.status === 404) {
        // Gracefully handle 404 by fetching all menu items
        console.warn(
          "Access roles not found (404). Fetching all menu items..."
        );
        await fetchData();
        setChecked({});
      } else {
        console.error("Error fetching access roles:", error.message);
        setError("Failed to load access roles. Please try again.");
      }
    }
  };

  useEffect(() => {
    fetchData();
    if (ID) fetchAccessRoles();
    setLoading(false);
  }, [ID]);

  const handleToggle = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleCheck = (id, isChecked, item) => {
    setChecked((prev) => {
      const updatedChecked = { ...prev, [id]: isChecked };

      setDescendantChecks(item, isChecked, updatedChecked);

      return updatedChecked;
    });
  };

  const handleSave = async () => {
    try {
      const authKey = localStorage.getItem("authKey");
      const selectedItems = Object.keys(checked).filter((key) => checked[key]);

      await axios.put(
        `${API_URL}/api/update_menu_access/${ID}`,
        { menu_item_ids: selectedItems },
        { headers: { Authorization: authKey } }
      );

      setSeverity("success");
      showSnackbar("Roles saved successfully");
      setTimeout(() => {
        navigate("/user");
      }, 1000);
    } catch (error) {
      console.error("Error updating access roles:", error.message);
      showSnackbar(
        error.response?.data?.message || "Failed to save menu access",
        "error"
      );
    }
  };

  if (loading) return <div className="loader-container">Loading...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  const handleCancelClick = () => {
    navigate("/user");
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <>
      <div
        className={`data-list-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-list-header">
          <div className="data-list-header-content">
            <PageBackTitle>User Access Table</PageBackTitle>
          </div>
        </div>

        <div className="data-list-body">
          <div className="data-list-scroll-body">
            <div className="data-list-table">
              <table className="table multilevel-data">
                <thead>
                  <tr>
                    <th className="table-header">Menu Items</th>
                  </tr>
                </thead>
                <tbody className="data-list-scroll-bodys">
                  {menuItems.map((item) => (
                    <MenuItem
                      key={item.id}
                      item={item}
                      level={0}
                      onToggle={handleToggle}
                      expanded={expanded}
                      checked={checked}
                      onCheck={handleCheck}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        {/* <footer className="user-access-footer">
          <div className="footer-buttons-container">
            <button onClick={handleSave} className="save-btn">
              Save Roles
            </button>
            <button
              className="cancel-btn"
              // onClick={handleCancelClick}
            >
              Cancel
            </button>
          </div>
        </footer> */}
        <div className="footer">
          <div className="button-group">
            <button
              className="data-form-save-button"
              type="button"
              onClick={handleSave}
              // disabled={isLoading}
            >
              Save Roles
            </button>
            <button
              className="data-form-cancel-button"
              type="button"
              onClick={handleCancelClick}
            >
              Cancel
            </button>
          </div>
        </div>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000} // Converted to milliseconds
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          style={{ marginTop: "35px", marginLeft: "20px" }}
        >
          <MuiAlert onClose={handleSnackbarClose} severity={severity}>
            {snackbarMessage}
          </MuiAlert>
        </Snackbar>
      </div>
    </>
  );
}

export default UserAccessTable;
